import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import {
  cloneElement,
  FocusEventHandler,
  forwardRef,
  memo,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useMemo
} from 'react'
import Tag from 'core/components/misc/Tag'
import { StandardOptionData } from 'core/components/dialogs/Menu'
import { ID } from 'types/entity'
import Icon from 'core/components/icons/Icon'
import { IconType } from 'core/components/icons/types'

export interface ComboboxProps<T extends { token_id: ID }> {
  name: string
  values: Array<
    | string
    | number
    | StandardOptionData<T>
    | ReactElement<{ value: T['token_id']; onClick: VoidFunction }>
  >
  onChange?: (values: Array<T['token_id'] | string | number>) => void
  suffix?: `${IconType}` | ReactElement
  prefix?: `${IconType}` | ReactElement
  className?: string
  onFocus?: FocusEventHandler
  onBlur?: FocusEventHandler
  disabled?: boolean
  invalid?: boolean
  status?: 'success' | 'error' | 'loading' | 'idle'
  readOnly?: boolean
  placeholder?: string
  children?: ReactElement<{
    value: any
    onChange: any
    name: string
  }>
  renderTag?: (
    option?:
      | string
      | number
      | StandardOptionData<T>
      | ReactElement<{ value: T['token_id']; onClick: VoidFunction }>
  ) => ReactNode
}

const Combobox = memo(
  forwardRef(
    <T extends { token_id: ID }>(
      {
        className,
        suffix,
        prefix,
        invalid = false,
        status = 'idle',
        disabled = false,
        readOnly = false,
        values = [],
        placeholder,
        onChange,
        onFocus,
        onBlur,
        children,
        name,
        renderTag = (v) => {
          if (!v) return null
          if (typeof v === 'string' || typeof v === 'number') return v
          if ('label' in v) return v.label
          return v.props.value
        }
      }: ComboboxProps<T>,
      ref: Ref<HTMLSpanElement>
    ) => {
      const statusStyles = useMemo((): Record<string, string | undefined> => {
        return {
          idle: 'focus-within:outline-primary-0 hover:outline-primary-0 outline-dark-2',
          success: '', // no status style
          error: 'text-danger-0 outline-danger-0',
          warning: 'outline-warning-2 text-warning-2'
        }
      }, [])

      const extractTokenId = useCallback((value: (typeof values)[0]) => {
        if (typeof value === 'string' || typeof value === 'number') return value
        if ('token_id' in value) return value.token_id
        return value.props.value
      }, [])

      const tagList = useMemo(() => {
        return values.map((val) => {
          const id = extractTokenId(val)
          const onSelect = () => {
            onChange?.(values.map(extractTokenId).filter((v) => v !== id))
          }
          if (
            typeof val === 'string' ||
            typeof val === 'number' ||
            'token_id' in val
          ) {
            const optionDisabled =
              typeof val === 'string' || typeof val === 'number'
                ? false
                : val.disabled
            return (
              <motion.div
                key={id}
                className="inline-block max-w-full grow-0"
                layout="position"
              >
                <Tag
                  value={id.toString()}
                  variant="primary"
                  className="w-full"
                  disabled={disabled}
                  suffix={
                    readOnly || disabled || optionDisabled ? undefined : (
                      <motion.div whileHover={{ scale: 1.1 }}>
                        <Icon
                          onClick={onSelect}
                          name="close"
                          noWrapper
                          size={12}
                        />
                      </motion.div>
                    )
                  }
                >
                  {renderTag(val) ?? id}
                </Tag>
              </motion.div>
            )
          }
          return (
            <motion.div key={id} layout="position">
              {cloneElement(val, {
                ...val.props,
                key: id,
                value: id.toString(),
                onClick: onSelect
              })}
            </motion.div>
          )
        })
      }, [values, extractTokenId, onChange, disabled, readOnly, renderTag])

      const prefixEl = useMemo(() => {
        if (!prefix) return null
        if (prefix && (prefix as string) in IconType) {
          return (
            <Icon
              name={prefix as IconType}
              width={12}
              height={12}
              className={cn([
                'group-focus-within:text-dark text-dark-3 mr-2 whitespace-nowrap my-1',
                statusStyles[
                  status !== 'idle' ? status : invalid ? 'error' : 'idle'
                ]
              ])}
            />
          )
        }

        return (
          <span
            className={cn([
              'group-focus-within:text-dark text-dark-3 mr-2 whitespace-nowrap my-1',
              statusStyles[
                status !== 'idle' ? status : invalid ? 'error' : 'idle'
              ]
            ])}
          >
            {prefix}
          </span>
        )
      }, [invalid, prefix, status, statusStyles])

      const suffixEl = useMemo(() => {
        const isCoreIcon = (suffix as string) in IconType

        if (status === 'idle' && suffix && isCoreIcon) {
          return (
            <Icon
              name={suffix as IconType}
              size={14}
              className="mb-px ml-2"
              noWrapper
            />
          )
        }
        if (status === 'idle' && suffix && !isCoreIcon) {
          return <span className="ml-2">{suffix}</span>
        }
        if (status === 'loading') {
          return (
            <Icon
              name="loading"
              size={14}
              className="mb-[3px] ml-2"
              noWrapper
            />
          )
        }
        if (status === 'success') {
          return (
            <Icon
              name="check-circle-outline"
              size={14}
              className="mb-[2px] ml-2"
              noWrapper
            />
          )
        }
        if (status === 'error') {
          return (
            <Icon
              name="exclamation-mark"
              size={14}
              className="mb-[2px] ml-2 text-white bg-danger rounded-full"
              noWrapper
            />
          )
        }

        return null
      }, [suffix, status])

      return (
        <motion.span
          layout="size"
          transition={{ type: 'tween', ease: 'easeOut' }}
          tabIndex={1000}
          onFocus={onFocus}
          onBlur={onBlur}
          ref={ref}
          className={cn([
            'group inline-flex items-baseline justify-between relative text-default rounded-md outline outline-1 px-2 p-1 overflow-clip gap-1',
            disabled || status === 'loading'
              ? 'bg-dark-1 text-dark-7 cursor-not-allowed pointer-events-none'
              : 'bg-white',
            statusStyles[
              status !== 'idle' ? status : invalid ? 'error' : 'idle'
            ],
            className
          ])}
        >
          {prefixEl}
          <motion.div
            className={cn('w-48 grow whitespace-nowrap flex flex-wrap gap-1')}
          >
            <input
              type="text"
              placeholder={placeholder}
              readOnly
              className={
                values.length > 0 || children
                  ? 'hidden'
                  : 'block appearance-none outline-none'
              }
              name={name}
              value={values.map(extractTokenId).map((a) => a.toString())}
            />
            <AnimatePresence initial={false} mode="popLayout">
              {tagList}
              <motion.div
                layout="position"
                className="inline-flex grow"
                key="$$children"
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </motion.div>
          <span
            className={cn([
              'group-focus:text-dark whitespace-nowrap',
              suffix && 'ml-2 my-1',
              status !== 'error' && !invalid && 'text-dark-3',
              statusStyles[
                status !== 'idle' ? status : invalid ? 'error' : 'idle'
              ]
            ])}
          >
            <AnimatePresence>{suffixEl}</AnimatePresence>
          </span>
        </motion.span>
      )
    }
  )
)

export default Combobox
